import { useEffect, useMemo, useState } from "react";

import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";
import { VerticalContainer } from "@shared/designSystem/Containers";
import { TabScreenSleek } from "@shared/designSystem/TabScreen";
import { Flap } from "@shared/models/Flap";
import designSystemStyles from "@shared/styles/designSystemStyles";

import ExportNovedadesEmployeesStepScreen from "./ExportNovedadesEmployeesStepScreen";
import ExportNovedadesEventsStepScreen from "./ExportNovedadesEventsStepScreen";
import ExportNovedadesExportStepScreen from "./ExportNovedadesExportStepScreen";
import ExportNovedadesSummaryStepScreen from "./ExportNovedadesSummaryStepScreen";

interface ExportNovedadesDetailScreenProps {
    process: NovedadesExportServiceModel.ProcessRecord;
    service: NovedadesExportService;
    onProcessRestarted?: (process: NovedadesExportServiceModel.ProcessRecord) => void;
    onFileGenerationStarted?: () => void;
}

const ExportNovedadesDetailScreen: React.FC<ExportNovedadesDetailScreenProps> = ({
    process,
    service,
    onProcessRestarted,
    onFileGenerationStarted,
}) => {
    const [clickedScreen, setClickedScreen] = useState("summary");
    const trackTabSelected = (tabID: string) => {
        if (tabID !== "summary" && tabID !== "events" && tabID !== "employees" && tabID !== "export") {
            return;
        }
        NovedadesExportAnalyticsHelper.trackDetailTabSelected({
            processID: process.processID,
            tabID,
            status: process.status,
            currentStep: process.currentStep,
        });
    };
    const handleTabSelected = (tabID: string) => {
        setClickedScreen(tabID);
        trackTabSelected(tabID);
    };

    useEffect(() => {
        trackTabSelected("summary");
    }, [process.processID]);

    const flaps = useMemo<Flap[]>(() => {
        return [
            {
                id: "summary",
                label: "Resumen".localized(),
                content: (
                    <ExportNovedadesSummaryStepScreen
                        process={process}
                        service={service}
                        onProcessRestarted={onProcessRestarted}
                    />
                ),
            },
            {
                id: "events",
                label: "Eventos".localized(),
                content: <ExportNovedadesEventsStepScreen process={process} service={service} />,
            },
            {
                id: "employees",
                label: "Colaboradores".localized(),
                content: <ExportNovedadesEmployeesStepScreen process={process} service={service} />,
            },
            {
                id: "export",
                label: "Exportación".localized(),
                content: (
                    <ExportNovedadesExportStepScreen
                        process={process}
                        service={service}
                        onFileGenerationStarted={onFileGenerationStarted}
                    />
                ),
            },
        ];
    }, [onProcessRestarted, process, service]);

    return (
        <VerticalContainer height="100%" gap={designSystemStyles.gapSm}>
            <TabScreenSleek
                title={process.title}
                flaps={flaps}
                clickedScreen={clickedScreen}
                setClickedScreen={handleTabSelected}
            />
        </VerticalContainer>
    );
};

export default ExportNovedadesDetailScreen;
